import { useQuery } from '@tanstack/react-query';

import { Download, ExternalLink } from '@repo/media/icons';

import { Button } from '@repo/components/button';
import { Card } from '@repo/components/card';
import { LoadingIndicator } from '@repo/components/loading-indicator';

import { addToast } from '@repo/features/toast';

import type { GuideKey } from '~/services/api-docs/api-docs';
import { fetchGuidePdf, GUIDE_PDF_FILE_NAMES, queryGuide } from '~/services/api-docs/api-docs';

// One of the organisation's guides, rendered by the Authorization service with this
// organisation's tenant id and this environment's values. Shown in a sandboxed frame, with the
// same document as PDF.
export function GuideDocument({ organizationId, guide, note }: { organizationId: string; guide: GuideKey; note: string }) {
  const guideQuery = useQuery(queryGuide(organizationId, guide));

  const openInTab = () => {
    if (!guideQuery.data) {
      return;
    }
    const href = URL.createObjectURL(new Blob([guideQuery.data], { type: 'text/html' }));
    globalThis.open(href, '_blank', 'noopener');
    // Revoked when this page goes away, not on a timer: the new tab reloads from the blob URL.
    globalThis.addEventListener('pagehide', () => {
      URL.revokeObjectURL(href);
    }, { once: true });
  };

  const downloadPdf = async () => {
    try {
      const href = URL.createObjectURL(await fetchGuidePdf(organizationId, guide));
      const link = document.createElement('a');
      link.href = href;
      link.download = GUIDE_PDF_FILE_NAMES[guide];
      document.body.append(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(href);
    }
    catch {
      addToast({ variant: 'negative', title: 'Download failed', description: 'Could not fetch the guide as PDF.' });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-secondary-500">{note}</p>
        <div className="flex items-center gap-2">
          <Button variant="soft" color="secondary" size="sm" icon={{ component: <ExternalLink size={16} /> }} isDisabled={!guideQuery.data} action={openInTab}>
            Open in new tab
          </Button>
          <Button variant="soft" color="secondary" size="sm" icon={{ component: <Download size={16} /> }} action={() => { void downloadPdf(); }}>
            Download PDF
          </Button>
        </div>
      </div>

      {guideQuery.isLoading && (
        <div className="py-12 flex justify-center">
          <LoadingIndicator size={18} className="text-primary-700" />
        </div>
      )}

      {guideQuery.isError && (
        <Card className="items-start">
          <p className="text-secondary-500">Could not load the guide.</p>
          <Button variant="soft" color="secondary" size="sm" action={() => { void guideQuery.refetch(); }}>Retry</Button>
        </Card>
      )}

      {guideQuery.data && (
        <iframe
          title="Guide"
          srcDoc={guideQuery.data}
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          className="w-full h-[calc(100dvh-16rem)] min-h-[32rem] rounded-lg border border-secondary-200 bg-white"
        />
      )}
    </div>
  );
}
